import router from "@/router";
import Vue from "vue";
import {
  loginWithKeycloak,
  handleKeycloakCallback,
  refreshKeycloakToken,
  logoutKeycloak,
  getKeycloak,
  getKeycloakToken,
  restoreKeycloakSession,
} from "@/utils/keycloak";

export default {
  namespaced: true,
  state: {
    loginTime: parseInt(sessionStorage.getItem("login_time")) || 0,
    user: JSON.parse(sessionStorage.getItem("auth_user") || "{}"),
    loginType: sessionStorage.getItem("kc_login_type") || null,
    keycloakTokens: {
      accessToken: sessionStorage.getItem("kc_token") || null,
      refreshToken: sessionStorage.getItem("kc_refresh_token") || null,
      idToken: sessionStorage.getItem("kc_id_token") || null,
    },
  },

  getters: {
    getUser(state) {
      return state.user;
    },
    getAccessToken(state) {
      // Return Keycloak token if available, otherwise BOS-ID token
      return state.keycloakTokens.accessToken || state.user.token;
    },
    getLoginType(state) {
      return state.loginType;
    },
    getKeycloakRefreshToken(state) {
      return state.keycloakTokens.refreshToken;
    },
    isKeycloakAuth(state) {
      return state.loginType === "keycloak_oauth2";
    },
  },

  mutations: {
    setLoginType(state, loginType) {
      state.loginType = loginType;
      // ✅ persistieren
      if (loginType) {
        sessionStorage.setItem("kc_login_type", loginType);
      } else {
        sessionStorage.removeItem("kc_login_type");
      }
    },
    setUser(state, user) {
      state.user = user;
      if (user && Object.keys(user).length > 0) {
        sessionStorage.setItem("auth_user", JSON.stringify(user));
      } else {
        sessionStorage.removeItem("auth_user");
      }
    },
    setAccessToken(state, token) {
      state.user.token = token;
    },

    setLoginTime(state, value = null) {
      state.loginTime = value ?? Math.round(new Date().getTime() / 1000);
      if (state.loginTime) {
        sessionStorage.setItem("login_time", state.loginTime);
      } else {
        sessionStorage.removeItem("login_time");
      }
    },
    setKeycloakTokens(state, tokens) {
      state.keycloakTokens = {
        accessToken: tokens.access_token || null,
        refreshToken: tokens.refresh_token || null,
        idToken: tokens.id_token || null,
      };
      if (tokens.access_token)
        sessionStorage.setItem("kc_token", tokens.access_token);
      if (tokens.refresh_token)
        sessionStorage.setItem("kc_refresh_token", tokens.refresh_token);
      if (tokens.id_token)
        sessionStorage.setItem("kc_id_token", tokens.id_token);
    },
    clearKeycloakTokens(state) {
      state.keycloakTokens = {
        accessToken: null,
        refreshToken: null,
        idToken: null,
      };
      // ✅ aufräumen
      sessionStorage.removeItem("kc_token");
      sessionStorage.removeItem("kc_refresh_token");
      sessionStorage.removeItem("kc_id_token");
      sessionStorage.removeItem("kc_login_type");
    },
  },

  actions: {
    /**
     * Get login type for email (Step 1 of two-step login)
     * @param {string} email - User's email address
     * @returns {Promise<string>} Login type: 'local' or 'keycloak_oauth2'
     */
    async getLoginType(ctx, email) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const url = "/api/v2/accounts/login-type";

      try {
        const response = await Vue.prototype.axios({
          method: "post",
          url: url,
          data: { email },
          headers: headers,
        });

        const loginType = response.data.login_type || "local";
        ctx.commit("setLoginType", loginType);
        return loginType;
      } catch (error) {
        throw error;
      }
    },

    /**
     * Local login (Step 2a - for local auth type)
     * @param {Object} account - Account credentials (email, password)
     */
    async createLogin(ctx, account) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const url = "/api/v2/accounts/login";
      return await Vue.prototype
        .axios({
          method: "post",
          url: url,
          data: {
            email: account.email,
            password: account.password,
          },
          headers: headers,
        })
        .then(async (resp) => {
          const user = resp.data;
          ctx.commit("setUser", user);
          ctx.commit("setLoginTime");
          ctx.commit("setLoginType", "local");
          try {
            await ctx.dispatch("fetchUserInfo");
          } catch (error) {
            // Rechte konnten nicht nachgeladen werden, Login bleibt trotzdem gültig
          }
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error)
          return error.response.status;
        });
    },

    /**
     * Initiate Keycloak OAuth flow (Step 2b - for keycloak_oauth2 auth type)
     * @param {string} email - User's email for login hint
     */
    async initiateKeycloakLogin(ctx, email) {
      // keycloak-js handles PKCE automatically
      await loginWithKeycloak(email);
      // Note: This will redirect to Keycloak, so code after this won't execute
    },

    /**
     * Complete Keycloak OAuth flow (handle callback)
     */
    async completeKeycloakLogin(ctx) {
      try {
        // keycloak-js handles the callback automatically
        const result = await handleKeycloakCallback();
        if (result.authenticated) {
          // Store Keycloak tokens
          ctx.commit("setKeycloakTokens", {
            access_token: result.token,
            refresh_token: result.refreshToken,
            id_token: result.idToken,
          });
          ctx.commit("setLoginType", "keycloak_oauth2");
          ctx.commit("setLoginTime");
          await ctx.dispatch("fetchUserInfo");
          return result;
        } else {
          throw new Error("Authentication failed");
        }
      } catch (error) {
        throw error;
      }
    },

    /**
     * Versucht eine bestehende Keycloak-Session aus sessionStorage wiederherzustellen.
     * Gibt true zurück wenn erfolgreich (User kann direkt zum Dashboard).
     */
    async tryRestoreKeycloakSession(ctx) {
      try {
        const restored = await restoreKeycloakSession();
        if (!restored) return false;

        const keycloak = getKeycloak();
        ctx.commit("setKeycloakTokens", {
          access_token: keycloak.token,
          refresh_token: keycloak.refreshToken,
          id_token: keycloak.idToken,
        });
        ctx.commit("setLoginType", "keycloak_oauth2");
        ctx.commit("setLoginTime");
        await ctx.dispatch("fetchUserInfo");
        return true;
      } catch {
        return false;
      }
    },

    /**
     * Fetch user information from backend
     */

    async fetchUserInfo(ctx) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const response = await Vue.prototype.axios({
        method: "get",
        url: `/api/v2/accounts/me`,
        headers,
      });
      // Mergen statt überschreiben: lokaler Login-Token (state.user.token) darf
      // nicht verloren gehen, falls /me ihn nicht mitliefert.
      ctx.commit("setUser", { ...ctx.state.user, ...response.data });
      return response.data;
    },
    async changePassword(ctx, data) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const url = "/api/v1/accounts/password";
      return await Vue.prototype
        .axios({
          method: "put",
          url: url,
          data: {
            email: data.email,
            password: data.password_new,
          },
          headers: headers,
        })
        .then((resp) => {
          // // console.log(error(resp)
          ctx.dispatch("makeLogout");
        })
        .catch((error) => {
          // // console.log(error)
        });
    },

    async refreshToken(ctx) {
      // Handle Keycloak token refresh
      if (ctx.getters.isKeycloakAuth) {
        try {
          const keycloak = getKeycloak();

          if (!keycloak) {
            throw new Error("Keycloak nicht initialisiert");
          }

          const refreshed = await keycloak.updateToken(30);

          if (refreshed) {
            ctx.commit("setKeycloakTokens", {
              access_token: keycloak.token,
              refresh_token: keycloak.refreshToken,
              id_token: keycloak.idToken,
            });
          }

          return keycloak.token; // ✅ hier aufhören, nicht weiterlaufen
        } catch (error) {
          await ctx.dispatch("makeLogout");
          return; // ✅ nicht in den lokalen Pfad fallen
        }
      }

      // Handle local token refresh
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const validityMinutes = 60;
      const url = "/api/v1/accounts/token/refresh";

      return await Vue.prototype
        .axios({
          method: "post",
          headers: headers,
          url: url,
          data: {
            token: ctx.state.user.token,
            validityMinutes: validityMinutes,
          },
        })
        .then(async (resp) => {
          const user = resp.data;
          await ctx.commit("setUser", user);
          await ctx.commit("setLoginTime");
          ctx.commit("setAccessToken", resp.data.token);
        })
        .catch((error) => {});
    },
    async makeLogout(ctx) {
      // Keycloak logout
      if (ctx.state.loginType === "keycloak_oauth2") {
        // Store ZUERST leeren, damit beforeunload keinen lastLogout-Eintrag setzt
        ctx.commit("clearKeycloakTokens");
        ctx.commit("setLoginType", null);
        ctx.commit("setLoginTime", 0);
        ctx.commit("setUser", {});

        try {
          // keycloak-js löst Browser-Redirect aus → beforeunload feuert hier
          await logoutKeycloak(window.location.origin + "/login");
        } catch (error) {
          router.push({ name: "Login" });
        }
        return;
      }

      // Local logout
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const url = "/api/v1/accounts/logout";
      return await Vue.prototype
        .axios({
          method: "post",
          headers: headers,
          url: url,
        })
        .then(async (resp) => {
          localStorage.setItem("bos_logout_clean", "1");
          await ctx.commit("setLoginTime", 0);
          await ctx.commit("setUser", {});
          await ctx.commit("setLoginType", null);
          router.push({ name: "Login" });
        })
        .catch((error) => {
          // // console.log(error)
        });
    },

    async hasAdminPermission(ctx) {
      // const isAuthenticated = await ctx.dispatch(this.isAuthenticated)

      // if (isAuthenticated) {
      // Check for key
      if ("is_customer_admin" in ctx.state.user) {
        //  Check for Value
        if (ctx.state.user.is_customer_admin) {
          return true;
        }
      }
      // }

      return false;
    },

    async hasUserPermission(ctx, department) {
      if (ctx.state.user.is_customer_admin) {
        return true;
      } else {
        if ("organisation_mappings" in ctx.state.user) {
          return ctx.state.user.organisation_mappings.find((o) => {
            if (department == "Organizations" && o.organisation_permission) {
              return true;
            } else if (department == "Users" && o.account_permission) {
              return true;
            } else if (department == "IdCard" && o.id_card_permission) {
              return true;
            } else if (department == "Amendments" && o.id_card_permission) {
              return true;
            } else if (
              department == "UnapprovedIDCards" &&
              o.id_card_permission
            ) {
              return true;
            } else if (department == "ExpiredIDCards" && o.id_card_permission) {
              return true;
            } else {
              return false;
            }
          });
        }
        return false;
      }
    },

    async isAuthenticated(ctx) {
      // Keycloak: nur Token-Existenz prüfen, loginTime ist nach Reload 0
      if (ctx.getters.isKeycloakAuth) {
        return ctx.state.keycloakTokens.accessToken !== null;
      }

      // Lokaler Login: Session-Zeit prüfen
      const now = Math.round(new Date().getTime() / 1000);
      const sessionTime = 60 * 5;

      if (
        ctx.state.user.hasOwnProperty("token") &&
        now < ctx.state.loginTime + sessionTime
      ) {
        return true;
      }

      return false;
    },

    async resetPassword(ctx, email) {
      const url = "/api/v1/accounts/resetPassword";
      return await Vue.prototype
        .axios({
          method: "put",
          url: url,
          data: {
            email: email,
          },
        })
        .then((resp) => {
          router.push({ name: "Login" });
        })
        .catch((error) => {
          // // console.log(error)
        });
    },

    async getHeaders(ctx) {
      // Wenn Keycloak benutzt wird
      if (ctx.getters.isKeycloakAuth) {
        try {
          const keycloak = getKeycloak();

          // Falls Token fast abgelaufen -> automatisch refresh
          const minValidity = 30; // Sekunden
          const refreshed = await keycloak.updateToken(minValidity);

          if (refreshed) {
            // Token wurde aktualisiert → im Store persistieren
            ctx.commit("setKeycloakTokens", {
              access_token: keycloak.token,
              refresh_token: keycloak.refreshToken,
              id_token: keycloak.idToken,
            });
          }

          return {
            "X-Requested-With": "XMLHttpRequest",
            Authorization: `Bearer ${keycloak.token}`,
          };
        } catch (error) {
          await ctx.dispatch("makeLogout");
          return { "X-Requested-With": "XMLHttpRequest" };
        }
      }

      // Lokaler Auth-Token
      if (await ctx.dispatch("isAuthenticated")) {
        return {
          "X-Requested-With": "XMLHttpRequest",
          Authorization: `Bearer ${ctx.getters["getAccessToken"]}`,
        };
      }

      return {
        "X-Requested-With": "XMLHttpRequest",
      };
    },

    async getHeadersUpload(ctx) {
      if (await ctx.dispatch("isAuthenticated")) {
        return {
          "content-type": "application/octet-stream",
          "X-Requested-With": "XMLHttpRequest",
          Authorization: `Bearer ${ctx.getters["getAccessToken"]}`,
        };
      } else {
        return {
          "X-Requested-With": "XMLHttpRequest",
        };
      }
    },
  },
};
